import React, { useEffect, useState } from 'react';
import { authClient, Membership } from './authClient';
import { TokenSession } from './tokenSession';

// Lets a person who belongs to several businesses move between them without signing
// in again (specs/003-multi-tenancy). Hidden when there is only one.

interface Props {
  session: TokenSession;
  onSwitch: (next: TokenSession) => void;
}

const BusinessSwitcher: React.FC<Props> = ({ session, onSwitch }) => {
  const [businesses, setBusinesses] = useState<Membership[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    authClient
      .session()
      .then((signedIn) => {
        if (live) {
          setBusinesses(signedIn.memberships.filter((m) => m.status === 'active'));
        }
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  if (businesses.length < 2) {
    return null;
  }

  const choose = async (accountId: string) => {
    if (accountId === session.accountId) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onSwitch(await TokenSession.start(accountId));
    } catch {
      setError('Could not open that business.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="business-switcher">
      <label className="sr-only" htmlFor="business-switcher">Business</label>
      <select
        id="business-switcher"
        value={session.accountId}
        disabled={busy}
        onChange={(event) => choose(event.target.value)}
      >
        {businesses.map((m) => (
          <option key={m.accountId} value={m.accountId}>{m.name}</option>
        ))}
      </select>
      {error && <span className="business-switcher-error" role="alert">{error}</span>}
    </div>
  );
};

export default BusinessSwitcher;
